import React, { useCallback, useEffect, useState } from 'react';
import Reactions from '../../components/Reactions';
import { apiFetch, getToken, API_BASE } from '../../services/apiClient';
import { itemEffectiveTotal, calcItem, STATUS_META, itemEffectiveUnitPrice, pricedQty, unitPer, priceOverrideOf } from './AdminBidDetail';
import { classifyBid, priceStructureLabel, workTypeLabel } from '../../utils/bidTypes';
import QuoteImport from './QuoteImport';
import PoDetailModal, { PoShipBits } from './PoDetailModal';
import { QbTimeJob } from '../../components/JobLocationModal';

// ─────────────────────────────────────────────────────────────────────────────
// The pillows that were still inline in the 10,000-line job page, pulled out one
// per component. Each calls the SAME endpoint the old page calls, so the two show
// the same thing.
//
// They're small and separate on purpose: a pillow can now be read, changed or
// dropped on its own, which is exactly what the old page made impossible.
// ─────────────────────────────────────────────────────────────────────────────

const fld = { border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box', background: '#fff' };
const btn = { padding: '8px 14px', borderRadius: 7, border: 'none', background: '#e87722', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' };
const muted = { fontSize: 13, color: '#9ca3af' };
const rowBox = { background: '#fff', padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 };
const linkBtn = { background: 'none', border: 'none', padding: 0, color: '#c2410c', fontWeight: 700, fontSize: 13, cursor: 'pointer' };

const money = n => (n == null || n === '') ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const shortDate = s => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

// Each pillow loads its own data when it is opened, not when the page loads —
// thirty-one pillows all fetching at once would hammer the API for content nobody
// has looked at.
function useLoad(path, deps = []) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const reload = useCallback(() => {
    if (!path) return Promise.resolve();
    return apiFetch(path).then(setData).catch(e => setErr(e?.error || 'Could not load this.'));
  }, [path]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, deps);
  return { data, err, reload };
}

function Rows({ children, empty }) {
  const list = React.Children.toArray(children);
  if (!list.length) return <div style={muted}>{empty}</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: '#f3f4f6', border: '1px solid #f3f4f6', borderRadius: 8, overflow: 'hidden' }}>
      {list}
    </div>
  );
}

function AddRow({ value, onChange, onAdd, placeholder, label = 'Add' }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <input style={fld} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        onKeyDown={e => { if (e.key === 'Enter') onAdd(); }} />
      <button style={btn} onClick={onAdd} disabled={!value.trim()}>{label}</button>
    </div>
  );
}

// ── Job Information ──────────────────────────────────────────────────────────

export function HeadlinePillow({ bid, items }) {
  // The bid total is priced, not stored — it's the sum of each line's effective
  // total. Reuse the old page's own function rather than reimplementing the pricing:
  // a bid total that's subtly wrong is far worse than one that's missing.
  const total = items.reduce((s, i) => s + (itemEffectiveTotal(i, bid) || 0), 0);
  // Hours are quantity × hours-per-unit. The old page had a bug here once, summing
  // per-unit hours without multiplying by quantity, which read 982 on a bid carrying
  // 24,207 hours.
  const hours = items.reduce((s, i) => s + (Number(i.labor_hours) || 0) * (Number(i.quantity) || 0) / unitPer(i.unit), 0);
  const tiles = [
    ['Total Bid Value', items.length ? money(total) : '—'],
    ['Line Items', items.length],
    ['Total Hours', Math.round(hours).toLocaleString('en-US') + ' hrs'],
    ['Profit %', bid?.profit_pct != null ? bid.profit_pct + '%' : '—'],
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 10 }}>
      {tiles.map(([k, v]) => (
        <div key={k} style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: '11px 13px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>{k}</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginTop: 3 }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

// ── Cost summary ─────────────────────────────────────────────────────────────
// The bid's cost stack rolled up from its line items — the same figures the bid page's
// Bid Summary shows, from the same per-line pricing (calcItem), so the job never shows
// a number the estimate doesn't. Office-side: this is where the margin lives.
export function costStack(bid, items) {
  const z = { material: 0, matTax: 0, prodHours: 0, supHours: 0, labor: 0, subs: 0, ohMat: 0, ohLab: 0, cost: 0, profit: 0, selling: 0 };
  const add = (row, times) => {
    const c = calcItem(row, bid); const n = times || 1;
    z.material += (c.matBase || 0) * n; z.matTax += (c.matTax || 0) * n;
    z.prodHours += (c.prodHours || 0) * n; z.supHours += (c.supHours || 0) * n;
    z.labor += (c.laborTotal || 0) * n; z.subs += (c.dje || 0) * n;
    z.ohMat += (c.ohMat || 0) * n; z.ohLab += (c.ohLab || 0) * n;
    z.cost += (c.totalCost || 0) * n; z.profit += (c.profit || 0) * n; z.selling += (c.selling || 0) * n;
  };
  (items || []).forEach(it => {
    const subs = Array.isArray(it.sub_items) ? it.sub_items : [];
    if (subs.length) subs.forEach(s => add(s, pricedQty(s))); else add(it, pricedQty(it));
  });
  const total = (items || []).reduce((s, i) => s + (itemEffectiveTotal(i, bid) || 0), 0);
  const materialTotal = z.material + z.matTax;
  return { ...z, materialTotal, prime: materialTotal + z.labor + z.subs, overhead: z.ohMat + z.ohLab, rounding: total - z.selling,
    margin: total > 0 ? (z.profit / total) * 100 : 0, hours: z.prodHours + z.supHours, total };
}

export function CostSummaryPillow({ bid, items }) {
  if (!bid) return <div style={muted}>This job has no bid behind it, so there is no estimate to roll up.</div>;
  if (bid.pricing_type === 'tm') return <div style={muted}>Time &amp; Material job — the rates live on the T&amp;M Rates pillow and what's billed on T&amp;M Billing.</div>;
  if (!items || !items.length) return <div style={muted}>No line items on this bid yet.</div>;
  const S = costStack(bid, items);
  const pct = v => S.total > 0 ? ((v / S.total) * 100).toFixed(0) + '%' : '—';
  const hrs = h => Math.round(h).toLocaleString('en-US') + ' hrs';
  const Row = ({ label, value, sub, bold, top, accent }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: bold ? '8px 0' : '4px 0', borderTop: top ? '1px solid #e5e7eb' : 'none' }}>
      <span style={{ fontSize: bold ? 13 : 12.5, fontWeight: bold ? 800 : 600, color: bold ? '#111827' : '#4b5563' }}>{label}</span>
      {sub && <span style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</span>}
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: bold ? 15 : 13, fontWeight: bold ? 800 : 700, color: accent || (bold ? '#111827' : '#374151'), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
        {[['Bid Total', money(S.total)], ['Total Hours', hrs(S.hours)], ['Margin', S.margin.toFixed(1) + '%'], ['Target Profit', bid.profit_pct != null ? bid.profit_pct + '%' : '—']].map(([k, v]) => (
          <div key={k} style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: '9px 11px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>{k}</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px' }}>
        <Row label="Material" sub={`incl. tax · ${pct(S.materialTotal)}`} value={money(S.materialTotal)} />
        <Row label="Labor" sub={`${hrs(S.hours)} · ${pct(S.labor)}`} value={money(S.labor)} />
        <Row label="Subcontractors" sub={pct(S.subs)} value={money(S.subs)} />
        <Row label="Prime Cost" value={money(S.prime)} bold top />
        <Row label="Overhead" sub="material + labor" value={money(S.overhead)} />
        <Row label="Total Cost" value={money(S.cost)} bold top />
        <Row label="Profit" sub={`${S.margin.toFixed(1)}% of bid`} value={money(S.profit)} accent="#15803d" />
        {Math.abs(S.rounding) >= 0.5 && <Row label="Rounding" sub="from the Round column" value={(S.rounding > 0 ? '+' : '') + money(S.rounding)} />}
        <Row label="BID TOTAL" value={money(S.total)} bold top accent="#e87722" />
      </div>
      <div style={{ fontSize: 11.5, color: '#9ca3af' }}>Mark-ups: material OH {bid.overhead_material_pct ?? '—'}% · labor OH {bid.overhead_labor_pct ?? '—'}% · profit {bid.profit_pct ?? '—'}% · tax {bid.sales_tax_pct ?? '—'}% · supervision {bid.supervision_pct ?? '—'}% · labor ${bid.labor_rate ?? '—'}/hr. Change them on the bid.</div>
    </div>
  );
}

// ── Bid & Estimate ───────────────────────────────────────────────────────────
// The bid this job was awarded from, read as a card: what kind of bid, when it went in,
// who it went to, what it came to — with the way into the bid page, where the estimate
// itself (line items, mark-ups, alternates) is edited. The job never grows a second
// estimate editor; that stays on the bid.
//
// One pillow for the money now (Pat, Sep 2026): the headline numbers sit on top, the
// bid's facts under them, and the cost summary opens out with the pillow — three
// windows onto the same bid used to be three pillows. Line Items stays its own pillow.
// A T&M bid has no cost stack; its headline is the rate block.
export function BidEstimatePillow({ bid, items, project, pmName, navigate, expanded = false, onJobChanged }) {
  const { data: recips } = useLoad(bid?.id ? `/bids/${bid.id}/recipients` : null, [bid?.id]);
  // The owner comes from the bid; a job whose bid never had one (the work orders made
  // by hand) falls back to the job's own, and either can be typed in right here — it's
  // saved on both so every document that reads either agrees (Pat, Sep 22 2026).
  const [ownerDraft, setOwnerDraft] = useState(null);
  const ownerShown = bid?.client_name || project?.client_name || '';
  async function saveOwner() {
    const v = String(ownerDraft ?? '').trim(); setOwnerDraft(null);
    if (v === ownerShown) return;
    try {
      await apiFetch(`/bids/${bid.id}`, { method: 'PATCH', body: JSON.stringify({ client_name: v || null }) });
      if (project?.id) await apiFetch(`/projects/${project.id}`, { method: 'PATCH', body: JSON.stringify({ clientName: v || null }) });
      onJobChanged && onJobChanged();
    } catch { /* the page keeps what it has */ }
  }
  if (!bid) return <div style={muted}>This job has no bid behind it (a Trello-era job, or made by hand).</div>;
  const isTm = bid.pricing_type === 'tm';
  const cls = classifyBid(bid);
  const st = STATUS_META[bid.status] || { label: bid.status || '—', color: '#6b7280' };
  const total = (items || []).reduce((s, i) => s + (itemEffectiveTotal(i, bid) || 0), 0);
  const when = bid.bid_date ? new Date(String(bid.bid_date).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + (bid.bid_time ? ' · ' + bid.bid_time : '') : '—';
  const list = Array.isArray(recips) ? recips : [];
  const won = list.filter(r => r.used_us || r.low_bidder);
  const Fact = ({ k, v }) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>{k}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#111', marginTop: 2, wordBreak: 'break-word' }}>{v || <span style={{ color: '#c9ccd1' }}>—</span>}</div>
    </div>
  );
  const tmTiles = [
    ['Day rate', bid.tm_daily_rate != null ? money(bid.tm_daily_rate) : (bid.tm_rate_basis !== 'hourly' && bid.tm_rate != null ? money(bid.tm_rate) : '—')],
    ['Hour rate', bid.tm_hourly_rate != null ? money(bid.tm_hourly_rate) : (bid.tm_rate_basis === 'hourly' && bid.tm_rate != null ? money(bid.tm_rate) : '—')],
    ['Mobilization', bid.tm_mobilization != null ? money(bid.tm_mobilization) : '—'],
    ['Not to exceed', bid.tm_nte ? money(bid.tm_nte) : '—'],
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {isTm ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 10 }}>
          {tmTiles.map(([k, v]) => (
            <div key={k} style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: '11px 13px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>{k}</div>
              <div style={{ fontSize: 18, fontWeight: 700, marginTop: 3 }}>{v}</div>
            </div>
          ))}
        </div>
      ) : <HeadlinePillow bid={bid} items={items} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: '#111' }}>{bid.job_name || 'Bid'}</span>
        <span style={{ fontSize: 10.5, fontWeight: 800, color: '#fff', background: st.color, borderRadius: 999, padding: '2px 9px' }}>{st.label}</span>
        <span style={{ flex: 1 }} />
        {navigate && <button style={{ ...linkBtn, fontSize: 12.5 }} onClick={() => navigate(`/admin/bids/${bid.id}`)}>Open the bid page ›</button>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' }}>
        <Fact k="Bid type" v={bid.pricing_type === 'tm' ? 'Time & Material' : 'Hard Bid'} />
        {bid.pricing_type !== 'tm' && <Fact k="Price structure" v={priceStructureLabel(cls.price_structure)} />}
        <Fact k="Work type" v={workTypeLabel(cls.work_type)} />
        <Fact k="Bid date" v={when} />
        <Fact k="Project Manager" v={pmName ? pmName(bid.bid_pm_id) : bid.bid_pm_name} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>Owner</div>
          {ownerDraft === null ? (
            <button onClick={() => setOwnerDraft(ownerShown)} title="Click to set the owner"
              style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: ownerShown ? '#111' : '#9ca3af', marginTop: 2, wordBreak: 'break-word', font: 'inherit' }}>
              {ownerShown || 'Set the owner…'}
            </button>
          ) : (
            <input autoFocus value={ownerDraft} onChange={e => setOwnerDraft(e.target.value)} onBlur={saveOwner}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setOwnerDraft(null); }}
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e87722', borderRadius: 6, padding: '3px 6px', fontSize: 13, fontWeight: 600, marginTop: 2 }} />
          )}
        </div>
        <Fact k="Estimate total" v={items && items.length ? money(total) : (bid.tm_nte ? money(bid.tm_nte) + ' NTE' : null)} />
        <Fact k="Line items" v={items && items.length ? items.length.toLocaleString('en-US') : null} />
        <Fact k={won.length ? 'Awarded through' : 'Bid to'} v={(won.length ? won : list).map(r => r.client_name).filter(Boolean).join(', ') || (project?.awarded_by_company || null)} />
      </div>
      {expanded && !isTm && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, margin: '4px 0 8px' }}>Cost summary</div>
          <CostSummaryPillow bid={bid} items={items} />
        </div>
      )}
      <div style={{ fontSize: 11.5, color: '#9ca3af' }}>The estimate itself — line items, mark-ups, alternates, the quote PDF — is edited on the bid page. Changes there show here and on Line Items.{!expanded && !isTm ? ' Open this out for the cost summary.' : ''}</div>
    </div>
  );
}

// ── QuickBooks Time ──────────────────────────────────────────────────────────
// The job's project report from QuickBooks Time: every hour clocked to its job code
// between the job's start date and today, by person and by day. Read live from the
// timesheets (cached a few minutes on the server), so it is what payroll sees.
export function TsheetsPillow({ project, expanded = false }) {
  const today = new Date().toISOString().slice(0, 10);
  const defFrom = project?.start_date ? String(project.start_date).slice(0, 10) : `${today.slice(0, 4)}-01-01`;
  const [from, setFrom] = useState(defFrom);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('people'); // 'people' | 'days' | 'codes'
  const load = useCallback(() => {
    if (!project?.id) return;
    setLoading(true); setErr('');
    apiFetch(`/projects/${project.id}/tsheets-report?from=${from}&to=${to}`)
      .then(setData).catch(e => setErr(e?.error || 'Could not reach QuickBooks Time.')).finally(() => setLoading(false));
  }, [project?.id, from, to]);
  useEffect(() => { load(); }, [load]);
  const fmtH = h => (Number(h) || 0).toLocaleString('en-US', { maximumFractionDigits: 1 });
  const fmtD = d => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
  const dateInp = { ...fld, width: 'auto', padding: '5px 8px', fontSize: 12 };
  const tab = (k, l) => (
    <button key={k} onClick={() => setView(k)} style={{ padding: '4px 10px', borderRadius: 999, border: '1px solid ' + (view === k ? '#111' : '#e5e7eb'), background: view === k ? '#111' : '#fff', color: view === k ? '#fff' : '#374151', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>{l}</button>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12, color: '#6b7280' }}>
        <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} style={dateInp} />
        <span>to</span>
        <input type="date" value={to} min={from} max={today} onChange={e => setTo(e.target.value)} style={dateInp} />
        <button onClick={load} disabled={loading} title="Read it again from QuickBooks Time" style={{ ...linkBtn, fontSize: 12 }}>{loading ? 'Reading…' : '↻ Refresh'}</button>
        {project?.start_date && from !== defFrom && <button onClick={() => { setFrom(defFrom); setTo(today); }} style={{ ...linkBtn, fontSize: 12, color: '#6b7280' }}>since job start</button>}
      </div>
      {err && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 600 }}>{err}</div>}
      {!err && data && !data.jobcode && (
        <div style={muted}>No QuickBooks Time job code matches <b>{data.project?.number || 'this job'}</b> yet. Send it from here — then the crew can clock into it and its hours show here.</div>
      )}
      {/* The 📍 Location window's QuickBooks Time button, here too (Ryan, Oct 14 2026) — while there is
          no job code yet, and always when the tile is opened out (rename it, add its location). */}
      {!err && data && project?.id && (!data.jobcode || expanded) && <QbTimeJob projectId={project.id} inTile onDone={load} />}
      {data && data.jobcode && (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8 }}>
          {[['Total hours', fmtH(data.totalHours)], ['People', data.users.length], ['Days worked', data.days.length], ['Last day', data.days.length ? fmtD(data.days[data.days.length - 1].date) : '—']].map(([k, v]) => (
            <div key={k} style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: '9px 11px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>{k}</div>
              <div style={{ fontSize: 17, fontWeight: 800, marginTop: 2 }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {tab('people', 'By person')}{tab('days', 'By day')}{data.jobcodes.length > 1 && tab('codes', 'By job code')}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af' }}>{data.jobcode.name}{data.jobcode.includes ? ` +${data.jobcode.includes} sub-code${data.jobcode.includes === 1 ? '' : 's'}` : ''}{data.openShifts ? ` · ${data.openShifts} on the clock now` : ''}</span>
        </div>
        {view === 'people' && (
          <Rows empty="Nobody has clocked time to this job in that range.">
            {(expanded ? data.users : data.users.slice(0, 12)).map(u => (
              <div key={u.tsUserId} style={rowBox}>
                <div style={{ flex: 1, fontWeight: 600, color: '#111' }}>{u.name}</div>
                <div style={{ ...muted, fontSize: 12 }}>{u.days} day{u.days === 1 ? '' : 's'} · {fmtD(u.first)}{u.first !== u.last ? ' – ' + fmtD(u.last) : ''}</div>
                <div style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', minWidth: 64, textAlign: 'right' }}>{fmtH(u.hours)} hrs</div>
              </div>
            ))}
          </Rows>
        )}
        {view === 'days' && (
          <Rows empty="No days in that range.">
            {(expanded ? data.days : data.days.slice(-14)).slice().reverse().map(d => (
              <div key={d.date} style={rowBox}>
                <div style={{ flex: 1, fontWeight: 600, color: '#111' }}>{new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</div>
                <div style={{ ...muted, fontSize: 12 }}>{d.people} {d.people === 1 ? 'person' : 'people'}</div>
                <div style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', minWidth: 64, textAlign: 'right' }}>{fmtH(d.hours)} hrs</div>
              </div>
            ))}
          </Rows>
        )}
        {view === 'codes' && (
          <Rows empty="—">
            {data.jobcodes.map(c => (
              <div key={c.id} style={rowBox}>
                <div style={{ flex: 1, fontWeight: 600, color: '#111' }}>{c.name}</div>
                <div style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', minWidth: 64, textAlign: 'right' }}>{fmtH(c.hours)} hrs</div>
              </div>
            ))}
          </Rows>
        )}
        {!expanded && ((view === 'people' && data.users.length > 12) || (view === 'days' && data.days.length > 14)) && <div style={{ ...muted, fontSize: 11.5 }}>Open the pillow full width to see the whole list.</div>}
        <div style={{ fontSize: 11, color: '#9ca3af' }}>{data.sheets.toLocaleString('en-US')} timesheet{data.sheets === 1 ? '' : 's'} · read from QuickBooks Time, refreshed every few minutes.</div>
      </>)}
      {!data && !err && loading && <div style={muted}>Reading QuickBooks Time…</div>}
    </div>
  );
}

export function NotesPillow({ projectId }) {
  const { data, reload } = useLoad(`/projects/${projectId}/notes`, [projectId]);
  const [text, setText] = useState('');
  const list = Array.isArray(data) ? data : [];
  async function add() {
    if (!text.trim()) return;
    try {
      await apiFetch(`/projects/${projectId}/notes`, { method: 'POST', body: JSON.stringify({ note: text.trim() }) });
      setText(''); await reload();
    } catch { /* the note stays in the box rather than vanishing */ }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <AddRow value={text} onChange={setText} onAdd={add} placeholder="Add a note…" />
      <Rows empty="No notes on this job yet.">
        {list.slice(0, 25).map(n => (
          <div key={n.id} style={{ ...rowBox, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>{n.note || n.text || n.notes}{n.from_label && <span style={{ display: 'inline-block', marginLeft: 8, fontSize: 10.5, fontWeight: 800, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '1px 8px', verticalAlign: 'middle' }}>from {n.from_label}</span>}</div>
            <div style={{ ...muted, whiteSpace: 'nowrap' }}>{n.author_name || [n.first_name, n.last_name].filter(Boolean).join(' ') || 'Someone'} · {shortDate(n.created_at)}</div>
          </div>
        ))}
      </Rows>
    </div>
  );
}

// Who's signed in — the person who wrote an update can fix it; the office can too.
const sessionMe = () => { try { const s = JSON.parse(localStorage.getItem('titan_session') || 'null'); return { id: String(s?.user?.id ?? ''), role: s?.role || s?.user?.role || '' }; } catch { return { id: '', role: '' }; } };
const OFFICE = ['admin', 'Admin', 'ProjectManager', 'OfficeStaff', 'Accounting', 'Executive'];

export function DailyUpdatesPillow({ projectId }) {
  const { data, reload } = useLoad(`/projects/${projectId}/daily-updates`, [projectId]);
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(null); // { id, text }
  const list = Array.isArray(data) ? data : [];
  const me = sessionMe();
  const canEdit = u => u.kind !== 'system' && (String(u.author_id) === me.id || me.id === 'admin' || OFFICE.includes(me.role));
  async function post() {
    if (!text.trim()) return;
    try {
      await apiFetch(`/projects/${projectId}/daily-updates`, { method: 'POST', body: JSON.stringify({ notes: text.trim() }) });
      setText(''); await reload();
    } catch { /* leave the text alone so nothing is lost */ }
  }
  async function saveEdit() {
    if (!editing || !editing.text.trim()) return;
    try {
      await apiFetch(`/daily-updates/${editing.id}`, { method: 'PATCH', body: JSON.stringify({ notes: editing.text.trim() }) });
      setEditing(null); await reload();
    } catch { /* keep the editor open */ }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <AddRow value={text} onChange={setText} onAdd={post} placeholder="How did it go today?" label="Post" />
      <Rows empty="No updates on this job yet.">
        {list.slice(0, 30).map(u => (
          <div key={u.id} style={{ ...rowBox, alignItems: 'flex-start', background: u.kind === 'system' ? '#fffbeb' : '#fff' }}>
            {editing && editing.id === u.id ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <textarea autoFocus value={editing.text} onChange={e => setEditing(ed => ({ ...ed, text: e.target.value }))} rows={2}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveEdit(); if (e.key === 'Escape') setEditing(null); }}
                  style={{ ...fld, resize: 'vertical', fontFamily: 'inherit' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{ ...btn, background: '#1e3a5f', padding: '6px 14px' }} onClick={saveEdit} disabled={!editing.text.trim()}>Save</button>
                  <button style={{ ...btn, background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', padding: '6px 12px' }} onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{u.kind === 'system' ? '📅 ' : ''}{u.notes}</div>
                {/* Posted on a workflow card or a work order under this job — it came UP from there. */}
                {u.from_label && <span style={{ display: 'inline-block', marginTop: 4, fontSize: 10.5, fontWeight: 800, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '1px 8px' }}>from the {u.from_label}</span>}
                {u.kind !== 'system' && <Reactions type="update" id={u.id} reactions={u.reactions} />}
              </div>
            )}
            <div style={{ ...muted, whiteSpace: 'nowrap' }}>{u.author_name} · {shortDate(u.created_at)}{u.edited_at ? ' · edited' : ''}</div>
            {canEdit(u) && !(editing && editing.id === u.id) && (
              <button onClick={() => setEditing({ id: u.id, text: u.notes })} title="Edit" style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', lineHeight: 1, padding: 0 }}>✎</button>
            )}
          </div>
        ))}
      </Rows>
      {list.length > 30 && <div style={muted}>Showing the 30 most recent of {list.length}.</div>}
    </div>
  );
}

// ── Work Flows ───────────────────────────────────────────────────────────────

export function PunchListPillow({ projectId }) {
  const { data, reload } = useLoad(`/projects/${projectId}/punch-list`, [projectId]);
  const [text, setText] = useState('');
  const list = Array.isArray(data) ? data : [];
  const open = list.filter(i => i.status !== 'complete').length;
  async function add() {
    if (!text.trim()) return;
    try {
      await apiFetch(`/projects/${projectId}/punch-list`, { method: 'POST', body: JSON.stringify({ description: text.trim() }) });
      setText(''); await reload();
    } catch {}
  }
  async function toggle(item) {
    const status = item.status === 'complete' ? 'open' : 'complete';
    try { await apiFetch(`/projects/${projectId}/punch-list/${item.id}`, { method: 'PATCH', body: JSON.stringify({ status }) }); await reload(); } catch {}
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <AddRow value={text} onChange={setText} onAdd={add} placeholder="Add a punch item…" />
      <Rows empty="Nothing on the punch list.">
        {list.map(i => (
          <label key={i.id} style={{ ...rowBox, cursor: 'pointer' }}>
            <input type="checkbox" checked={i.status === 'complete'} onChange={() => toggle(i)} />
            <span style={{ flex: 1, textDecoration: i.status === 'complete' ? 'line-through' : 'none', color: i.status === 'complete' ? '#9ca3af' : '#111' }}>
              {i.description}
            </span>
            {i.assigned_to && <span style={muted}>{i.assigned_to}</span>}
          </label>
        ))}
      </Rows>
      {list.length > 0 && <div style={muted}>{open} still open of {list.length}.</div>}
    </div>
  );
}

export function WorkOrdersPillow({ projectId, navigate, embedded }) {
  const { data } = useLoad(`/projects/${projectId}/work-orders`, [projectId]);
  const list = Array.isArray(data) ? data : [];
  return (
    <Rows empty="No work orders under this job.">
      {list.map(w => (
        <div key={w.id} style={rowBox}>
          <button style={linkBtn} onClick={() => navigate(embedded ? `/sup/jobs/${w.id}` : `/admin/jobs/${w.id}`)}>
            {w.project_number}
          </button>
          <span style={{ flex: 1 }}>{w.name}</span>
          <span style={muted}>{w.wo_status || w.status || ''}</span>
        </div>
      ))}
    </Rows>
  );
}

// Contract and Subcontracts have no content table behind them — they are notes and
// nothing else. That is why Contract sits empty on all 58 jobs that carry it.
export function NotesOnlyPillow({ projectId, sj, canEdit }) {
  const [notes, setNotes] = useState(sj?.notes || '');
  const [state, setState] = useState('');
  useEffect(() => { setNotes(sj?.notes || ''); }, [sj?.id, sj?.notes]);
  async function save() {
    setState('saving');
    try {
      await apiFetch(`/projects/${projectId}/sub-jobs/${sj.id}`, { method: 'PATCH', body: JSON.stringify({ notes }) });
      setState('saved'); setTimeout(() => setState(''), 1800);
    } catch { setState('failed'); }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <textarea style={{ ...fld, minHeight: 90, resize: 'vertical' }} value={notes} disabled={!canEdit}
        onChange={e => setNotes(e.target.value)} placeholder="Notes for this workflow…" />
      {canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button style={btn} onClick={save}>Save</button>
          {state === 'saved' && <span style={{ fontSize: 12, color: '#15803d' }}>Saved</span>}
          {state === 'failed' && <span style={{ fontSize: 12, color: '#b91c1c' }}>Could not save — try again.</span>}
        </div>
      )}
    </div>
  );
}

// ── Material and Equipment ───────────────────────────────────────────────────

export function PurchaseOrdersPillow({ projectId, canSeeMoney }) {
  const { data, reload } = useLoad(`/projects/${projectId}/purchase-orders`, [projectId]);
  const [openPo, setOpenPo] = useState(null);   // the orange number opens the PO card (Ryan, Oct 12 2026)
  const list = Array.isArray(data) ? data : [];
  const total = list.reduce((s, p) => s + (Number(p.cost) || 0), 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ overflowX: 'auto' }}>
        <Rows empty="No POs on this job.">
          {list.map(p => (
            <div key={p.id} style={rowBox}>
              <span onClick={() => setOpenPo(p)} title="Open the PO" style={{ fontWeight: 700, color: '#c2410c', whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(194,65,12,0.35)', textUnderlineOffset: 3 }}>#{p.po_number}</span>
              <span style={{ flex: 1, minWidth: 120 }}>{p.description}<PoShipBits po={p} compact /></span>
              <span style={{ ...muted, whiteSpace: 'nowrap' }}>{p.vendor || '—'}</span>
              {canSeeMoney && <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{money(p.cost)}</span>}
              <span style={{ ...muted, whiteSpace: 'nowrap' }}>{p.status}</span>
            </div>
          ))}
        </Rows>
      </div>
      {canSeeMoney && list.length > 0 && <div style={muted}>{list.length} POs · {money(total)} total</div>}
      {openPo && <PoDetailModal po={openPo} onClose={() => setOpenPo(null)} onSaved={() => reload()} onDeleted={() => reload()} />}
    </div>
  );
}

// ── Plans ────────────────────────────────────────────────────────────────────
// The reason this tile exists. On the old page these files sit inside the bid
// block, which the field never sees.
export function PlansPillow({ bid }) {
  const { data } = useLoad(bid?.id ? `/bids/${bid.id}/attachments` : null, [bid?.id]);
  const list = Array.isArray(data) ? data : [];
  if (!bid?.id) return <div style={muted}>No bid attached to this job, so there are no plans on file.</div>;
  return (
    <Rows empty="No plans or documents uploaded for this job.">
      {list.map(a => (
        <div key={a.id} style={rowBox}>
          <span style={{ flex: 1 }}>{a.original_name || a.filename}</span>
          {a.annotations && <span style={{ fontSize: 11, color: '#7c3aed', whiteSpace: 'nowrap' }}>marked up</span>}
          <a href={`${API_BASE}/bids/${bid.id}/attachments/${a.id}/download?token=${getToken()}`}
            target="_blank" rel="noreferrer"
            style={{ fontSize: 12, color: '#c2410c', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Open
          </a>
        </div>
      ))}
    </Rows>
  );
}

// ── Close Out ────────────────────────────────────────────────────────────────

export function CloseOutPillow({ projectId }) {
  const { data, reload } = useLoad(`/projects/${projectId}/closeout`, [projectId]);
  const [text, setText] = useState('');
  const list = Array.isArray(data) ? data : [];
  const done = list.filter(i => i.checked).length;
  async function add() {
    if (!text.trim()) return;
    try {
      await apiFetch(`/projects/${projectId}/closeout`, { method: 'POST', body: JSON.stringify({ description: text.trim() }) });
      setText(''); await reload();
    } catch {}
  }
  async function toggle(i) {
    try { await apiFetch(`/projects/${projectId}/closeout/${i.id}`, { method: 'PATCH', body: JSON.stringify({ checked: !i.checked }) }); await reload(); } catch {}
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <AddRow value={text} onChange={setText} onAdd={add} placeholder="Add a close-out item…" />
      <Rows empty="Nothing on the close-out list.">
        {list.map(i => (
          <label key={i.id} style={{ ...rowBox, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!i.checked} onChange={() => toggle(i)} />
            <span style={{ flex: 1, textDecoration: i.checked ? 'line-through' : 'none', color: i.checked ? '#9ca3af' : '#111' }}>{i.description}</span>
            {i.checked_by && <span style={muted}>{i.checked_by}</span>}
          </label>
        ))}
      </Rows>
      {list.length > 0 && <div style={muted}>{done} of {list.length} done.</div>}
    </div>
  );
}

// ── Bid pillows ──────────────────────────────────────────────────────────────

export function RecipientsPillow({ bid }) {
  const { data } = useLoad(bid?.id ? `/bids/${bid.id}/recipients` : null, [bid?.id]);
  const list = Array.isArray(data) ? data : [];
  if (!bid?.id) return <div style={muted}>No bid attached to this job.</div>;
  return (
    <Rows empty="Nobody was sent this bid.">
      {list.map(r => (
        <div key={r.id} style={rowBox}>
          <span style={{ flex: 1, minWidth: 120 }}>
            {r.client_name || '—'}
            {r.contact_name && <span style={{ ...muted, marginLeft: 6 }}>{r.contact_name}</span>}
          </span>
          {/* used_us marks the quote the job was actually won on — the thing the award
              confirm screen locks in. */}
          {(r.used_us || r.id === bid.used_quote_recipient_id) && (
            <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', whiteSpace: 'nowrap' }}>WON ON THIS QUOTE</span>
          )}
          {!!r.low_bidder && <span style={{ fontSize: 10, fontWeight: 700, color: '#1d4ed8', whiteSpace: 'nowrap' }}>LOW BIDDER</span>}
          <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{money(r.price_to_owner)}</span>
          <span style={{ ...muted, whiteSpace: 'nowrap' }}>{r.sent_at ? `sent ${shortDate(r.sent_at)}` : 'not sent'}</span>
        </div>
      ))}
    </Rows>
  );
}

// Read-only on purpose. The editable table — pricing, flags, comments, sub-items —
// is the most involved thing in the app and moves on its own, not bundled into a
// layout change.
// Who may change the contract lines here (Ryan, Oct 29 2026): the office and Executives.
const LINE_EDITORS = ['admin', 'Admin', 'Executive', 'Accounting', 'OfficeStaff'];
const myRole = () => { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role; } catch { return null; } };

export function LineItemsPillow({ items, bid, canSeeMoney, projectId, embedded, onImported }) {
  const hours = items.reduce((s, i) => s + (Number(i.labor_hours) || 0) * (Number(i.quantity) || 0) / unitPer(i.unit), 0);
  const canEditLines = canSeeMoney && !embedded && !!bid?.id && LINE_EDITORS.includes(myRole());
  const [editing, setEditing] = useState(false);
  if (editing) return <EditLines items={items} bid={bid} onChanged={onImported} onDone={() => setEditing(false)} />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* An older job that came over without its line items: bring them in from the old
          quote. Office side only, and only while there are none — the server refuses
          an import onto a job that already has lines. */}
      {items.length === 0 && canSeeMoney && !embedded && bid?.id && (
        <QuoteImport bidId={bid.id} projectId={projectId} onImported={onImported} />
      )}
      {/* A real table (Pat, 2633, Sep 2026): item, quantity, unit, unit price, extension,
          in squared-off columns with a total underneath. Money only for those who see it. */}
      {items.length === 0 ? <div style={muted}>No line items on this bid.</div> : (
        <div style={{ overflowX: 'auto' }}>
          <LineItemsTable items={items} bid={bid} canSeeMoney={canSeeMoney} />
        </div>
      )}
      {/* The count, and ✎ Edit lines beside it — where the old "Edit them there" note was (Ryan, Oct 29 2026). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ ...muted, flex: 1 }}>
          {items.length} line{items.length === 1 ? '' : 's'}{canSeeMoney && hours ? ` · ${Math.round(hours).toLocaleString('en-US')} hrs` : ''}
        </div>
        {canEditLines && (
          <button onClick={() => setEditing(true)} title="Change, add or delete the contract lines — every change is noted in the job's activity"
            style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: '#1e3a5f', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
            ✎ Edit lines
          </button>
        )}
      </div>
    </div>
  );
}

const qtyText = q => (q == null || q === '' ? '—' : Number(q).toLocaleString('en-US', { maximumFractionDigits: 2 }));
// The pay item in its own column (Ryan, 2610, Oct 14 2026): the line's pay item number, or the
// one typed at the front of its wording ("2020925: Removal of pull box") split off.
const PAY_IN_TEXT = /^\s*([A-Z]{0,3}\d{5,8}(?:[.-]\d{1,4})?[A-Z]?)\s*[:–—-]\s*(.+)$/;
const splitPayItem = i => {
  if (i.pay_item_number) return [String(i.pay_item_number), i.description || ''];
  const m = String(i.description || '').match(PAY_IN_TEXT);
  return m ? [m[1], m[2]] : ['', i.description || ''];
};
function LineItemsTable({ items, bid, canSeeMoney }) {
  const split = items.map(splitPayItem);
  const hasPay = split.some(([pay]) => pay);
  const payCol = hasPay ? ' 92px' : '';
  const cols = canSeeMoney ? `30px${payCol} minmax(180px, 1fr) 72px 60px 104px 116px` : `30px${payCol} minmax(180px, 1fr) 72px 60px`;
  const head = { fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, padding: '7px 10px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' };
  const cell = { padding: '8px 10px', fontSize: 13, borderBottom: '1px solid #f1f3f5', minWidth: 0 };
  const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  const total = items.reduce((s, i) => s + (Number(itemEffectiveTotal(i, bid)) || 0), 0);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: cols, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff', minWidth: (canSeeMoney ? 560 : 340) + (hasPay ? 92 : 0) }}>
      <div style={{ ...head, textAlign: 'right' }}>#</div>
      {hasPay && <div style={head}>Pay item</div>}
      <div style={head}>{hasPay ? 'Description' : 'Item'}</div>
      <div style={{ ...head, textAlign: 'right' }}>Qty</div>
      <div style={head}>Unit</div>
      {canSeeMoney && <div style={{ ...head, textAlign: 'right' }}>Unit price</div>}
      {canSeeMoney && <div style={{ ...head, textAlign: 'right' }}>Extension</div>}
      {items.map((i, n) => (
        <React.Fragment key={i.id || n}>
          <div style={{ ...num, color: '#9ca3af' }}>{i.item_number || n + 1}</div>
          {hasPay && <div style={{ ...cell, fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#c2410c', whiteSpace: 'nowrap' }}>{split[n][0] || ''}</div>}
          <div style={cell}><span style={{ color: '#111827' }}>{split[n][1] || '—'}</span></div>
          <div style={num}>{qtyText(i.quantity)}</div>
          <div style={{ ...cell, color: '#6b7280', whiteSpace: 'nowrap' }}>{i.unit || ''}</div>
          {canSeeMoney && <div style={num}>{money(itemEffectiveUnitPrice(i, bid))}</div>}
          {canSeeMoney && <div style={{ ...num, fontWeight: 700 }}>{money(itemEffectiveTotal(i, bid))}</div>}
        </React.Fragment>
      ))}
      {canSeeMoney && (
        <>
          <div style={{ ...cell, gridColumn: hasPay ? '1 / 7' : '1 / 6', textAlign: 'right', fontWeight: 800, color: '#374151', background: '#f8fafc', borderBottom: 'none' }}>Total</div>
          <div style={{ ...num, fontWeight: 800, color: '#111827', background: '#f8fafc', borderBottom: 'none' }}>{money(total)}</div>
        </>
      )}
    </div>
  );
}

// ── ✎ Edit lines (Ryan, Oct 29 2026) ─────────────────────────────────────────
// The contract lines as cells: type and leave the cell (or press Enter) to save that one change.
// + Add line at the bottom, 🗑 on each line. A line already on a pay application asks first —
// the applications keep their own figures; the next one starts from these.
const priceText = n => (n == null || !Number.isFinite(Number(n)) ? '' : Number(n).toFixed(2));
function LineCell({ value, onSave, align = 'left', width, placeholder = '', strong = false }) {
  const shown = value == null ? '' : String(value);
  const [v, setV] = useState(shown);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(shown); }, [shown]);
  const commit = async () => {
    if (v === shown || busy) return;
    setBusy(true);
    const ok = await onSave(v);
    setBusy(false);
    if (!ok) setV(shown);
  };
  return (
    <input value={v} placeholder={placeholder} disabled={busy}
      onChange={e => setV(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setV(shown); setTimeout(() => e.target.blur(), 0); } }}
      style={{ width: width || '100%', boxSizing: 'border-box', padding: '6px 7px', fontSize: 13, border: `1px solid ${v !== shown ? '#f59e0b' : '#e5e7eb'}`, borderRadius: 6,
        textAlign: align, fontWeight: strong ? 700 : 400, background: busy ? '#f9fafb' : '#fff', fontVariantNumeric: align === 'right' ? 'tabular-nums' : 'normal' }} />
  );
}
function EditLines({ items, bid, onChanged, onDone }) {
  const [billed, setBilled] = useState({});
  const okd = React.useRef(new Set());      // billed lines already OK'd this visit
  const blank = { item_number: '', pay_item_number: '', description: '', quantity: '1', unit: 'EA', price_override: '' };
  const [add, setAdd] = useState(blank);
  const [adding, setAdding] = useState(false);
  useEffect(() => { apiFetch(`/bids/${bid.id}/items/billed`).then(m => setBilled(m && typeof m === 'object' ? m : {})).catch(() => {}); }, [bid.id]);
  const appsOf = it => (billed[it.id] || []).map(a => `#${a.no}${a.status === 'draft' ? ' (draft)' : ''}`).join(', ');
  const okBilled = it => {
    if (!billed[it.id] || okd.current.has(it.id)) return true;
    if (!window.confirm(`This line is on pay application ${appsOf(it)}.\n\nThose applications keep the figures they were made with; the next one starts from the line as you change it now.\n\nChange it anyway?`)) return false;
    okd.current.add(it.id);
    return true;
  };
  async function save(it, field, value) {
    if (!okBilled(it)) return false;
    try {
      await apiFetch(`/bids/${bid.id}/items/${it.id}`, { method: 'PATCH', body: JSON.stringify({ [field]: value, was_unit_price: itemEffectiveUnitPrice(it, bid) }) });
      onChanged && onChanged();
      return true;
    } catch (e) { window.alert(e?.error || e?.message || 'That did not save.'); return false; }
  }
  async function del(it) {
    const label = [it.pay_item_number || it.item_number, it.description].filter(Boolean).join(' — ');
    const onApp = billed[it.id] ? `\n\nIt is on pay application ${appsOf(it)} — those keep their own figures.` : '';
    if (!window.confirm(`Delete this line?\n\n${label}${onApp}\n\nIt is noted in the job's activity.`)) return;
    try { await apiFetch(`/bids/${bid.id}/items/${it.id}`, { method: 'DELETE' }); onChanged && onChanged(); }
    catch (e) { window.alert(e?.error || e?.message || 'Could not delete it.'); }
  }
  async function addLine() {
    if (!add.description.trim()) { window.alert('Give the new line a description.'); return; }
    setAdding(true);
    try {
      await apiFetch(`/bids/${bid.id}/items/line`, { method: 'POST', body: JSON.stringify(add) });
      setAdd(blank);
      onChanged && onChanged();
    } catch (e) { window.alert(e?.error || e?.message || 'Could not add it.'); }
    setAdding(false);
  }
  const total = items.reduce((s, i) => s + (Number(itemEffectiveTotal(i, bid)) || 0), 0);
  const th = { fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, padding: '7px 6px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', textAlign: 'left', whiteSpace: 'nowrap' };
  const td = { padding: '4px 4px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'middle' };
  const inp = { width: '100%', boxSizing: 'border-box', padding: '6px 7px', fontSize: 13, border: '1px solid #c7d2fe', borderRadius: 6, background: '#fff' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px' }}>
        <div style={{ flex: 1, minWidth: 220, fontSize: 12.5, color: '#1e3a5f' }}>
          <b>Editing the contract lines.</b> Type in a cell and press Enter (or click away) to save it. Every change is noted in the job's activity.
        </div>
        <button onClick={onDone} style={{ padding: '7px 18px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>Done</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 820, borderCollapse: 'collapse', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 62 }}>#</th>
              <th style={{ ...th, width: 108 }}>Pay item</th>
              <th style={th}>Description</th>
              <th style={{ ...th, width: 84, textAlign: 'right' }}>Qty</th>
              <th style={{ ...th, width: 66 }}>Unit</th>
              <th style={{ ...th, width: 112, textAlign: 'right' }}>Unit price</th>
              <th style={{ ...th, width: 116, textAlign: 'right' }}>Extension</th>
              <th style={{ ...th, width: 34 }} />
            </tr>
          </thead>
          <tbody>
            {items.map(it => {
              const typed = priceOverrideOf(it) != null;
              return (
                <tr key={it.id}>
                  <td style={td}><LineCell value={it.item_number || ''} onSave={v => save(it, 'item_number', v)} /></td>
                  <td style={td}><LineCell value={it.pay_item_number || ''} onSave={v => save(it, 'pay_item_number', v)} /></td>
                  <td style={td}>
                    <LineCell value={it.description || ''} onSave={v => save(it, 'description', v)} />
                    {billed[it.id] && <div style={{ fontSize: 10.5, fontWeight: 800, color: '#b45309', padding: '2px 2px 0' }}>on pay application {appsOf(it)}</div>}
                  </td>
                  <td style={td}><LineCell value={it.quantity ?? ''} align="right" onSave={v => save(it, 'quantity', v)} /></td>
                  <td style={td}><LineCell value={it.unit || ''} onSave={v => save(it, 'unit', v)} /></td>
                  <td style={td}>
                    <LineCell value={priceText(itemEffectiveUnitPrice(it, bid))} align="right" onSave={v => save(it, 'price_override', v)} />
                    {typed && (
                      <button onClick={() => save(it, 'price_override', null)} title="Go back to the price worked out from cost and mark-ups"
                        style={{ ...linkBtn, fontSize: 10.5, color: '#6b7280', padding: '2px 2px 0' }}>↺ worked-out price</button>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', padding: '4px 8px', fontSize: 13 }}>{money(itemEffectiveTotal(it, bid))}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <button onClick={() => del(it)} title="Delete this line" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: '#dc2626', padding: 2 }}>🗑</button>
                  </td>
                </tr>
              );
            })}
            {/* The new line */}
            <tr style={{ background: '#f8fafc' }}>
              <td style={td}><input value={add.item_number} placeholder="#" onChange={e => setAdd(a => ({ ...a, item_number: e.target.value }))} style={inp} /></td>
              <td style={td}><input value={add.pay_item_number} placeholder="Pay item" onChange={e => setAdd(a => ({ ...a, pay_item_number: e.target.value }))} style={inp} /></td>
              <td style={td}><input value={add.description} placeholder="New line — description" onChange={e => setAdd(a => ({ ...a, description: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') addLine(); }} style={inp} /></td>
              <td style={td}><input value={add.quantity} onChange={e => setAdd(a => ({ ...a, quantity: e.target.value }))} style={{ ...inp, textAlign: 'right' }} /></td>
              <td style={td}><input value={add.unit} onChange={e => setAdd(a => ({ ...a, unit: e.target.value }))} style={inp} /></td>
              <td style={td}><input value={add.price_override} placeholder="0.00" onChange={e => setAdd(a => ({ ...a, price_override: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') addLine(); }} style={{ ...inp, textAlign: 'right' }} /></td>
              <td style={td} colSpan={2}>
                <button onClick={addLine} disabled={adding}
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: 'none', background: '#e87722', color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: adding ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                  {adding ? 'Adding…' : '+ Add line'}
                </button>
              </td>
            </tr>
            <tr>
              <td colSpan={6} style={{ padding: '9px 8px', textAlign: 'right', fontWeight: 800, color: '#374151', background: '#f8fafc' }}>Total · {items.length} line{items.length === 1 ? '' : 's'}</td>
              <td style={{ padding: '9px 8px', textAlign: 'right', fontWeight: 800, background: '#f8fafc', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(total)}</td>
              <td style={{ background: '#f8fafc' }} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Anything that genuinely has not been moved yet says so, rather than pretending.
export function NotYetMoved({ label, projectId, navigate, embedded }) {
  return (
    <div style={{ fontSize: 13, color: '#6b7280', background: '#fafafa', border: '1px dashed #e5e7eb', borderRadius: 8, padding: '12px 14px' }}>
      <b style={{ color: '#374151' }}>{label}</b> hasn’t been moved across yet — it still lives on the
      current job page. You can place it in a tile now and it’ll land there when it moves.
      <button style={{ ...linkBtn, fontSize: 13, marginLeft: 8, textDecoration: 'underline' }}
        onClick={() => navigate(embedded ? `/sup/old-job/${projectId}` : `/admin/old-job/${projectId}`)}>
        Open it there
      </button>
    </div>
  );
}

// ── Schedule ─────────────────────────────────────────────────────────────────
// Who is booked on this job over the next two weeks, from QuickBooks Time. The
// company-wide schedule page pulls every job; this is narrowed to one, so the job
// page can answer "who's on this next week" without loading the whole calendar.
export function SchedulePillow({ projectId }) {
  const { data, err } = useLoad(`/projects/${projectId}/schedule?days=14`, [projectId]);
  if (err) return <div style={muted}>Couldn’t load the schedule.</div>;
  if (!data) return <div style={muted}>Loading the schedule…</div>;
  if (data.error) {
    return <div style={muted}>QuickBooks Time didn’t answer, so the schedule isn’t showing. Everything else on this job is unaffected.</div>;
  }
  const events = Array.isArray(data.events) ? data.events : [];
  // Group by day so it reads like a schedule rather than a list of shifts.
  const byDay = {};
  for (const e of events) (byDay[e.date] = byDay[e.date] || []).push(e);
  const days = Object.keys(byDay).sort();
  if (!days.length) return <div style={muted}>Nobody is scheduled on this job in the next two weeks.</div>;
  const dayLabel = d => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {days.map(d => {
        const crew = [...new Set(byDay[d].flatMap(e => e.crew.map(c => c.name)))];
        const draft = byDay[d].some(e => e.draft);
        return (
          <div key={d}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 5 }}>
              {dayLabel(d)}
              {draft && <span style={{ marginLeft: 8, color: '#b45309', fontWeight: 600 }}>not published yet</span>}
            </div>
            <Rows empty="Nobody booked.">
              {crew.map(name => (
                <div key={name} style={rowBox}><span style={{ flex: 1 }}>{name}</span></div>
              ))}
            </Rows>
          </div>
        );
      })}
      <div style={muted}>Next 14 days · from QuickBooks Time.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Big Picture Schedule — when the job can actually run.
//
// Two dates, dirt and electrical, each with how long it is expected to take, and a
// "Ready now" flag for a job that could be worked today. This is NOT the crew
// schedule below it: that one answers "who is booked this week", this one answers
// "when is this job happening, and has it moved".
//
// Every change to a date is written to job_big_picture_changes with who made it and
// how many days it moved, and the history reads back underneath. That history is the
// whole point — a job quietly sliding a week at a time is invisible if all you ever
// see is today's date.
// ─────────────────────────────────────────────────────────────────────────────
const bpLbl = { fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 };

// Start date + duration gives the day it should be off the job, which is the date
// everyone downstream actually cares about.
function endsOn(date, days) {
  if (!date || !days) return null;
  const d = new Date(date + 'T00:00:00');
  d.setDate(d.getDate() + Math.max(0, Math.round(Number(days)) - 1));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function BigPictureLine({ label, tint, date, days, ready, filler, done, onSave, canEdit }) {
  const finish = endsOn(date, days);
  return (
    <div style={{ border: '1px solid #e5e7eb', borderLeft: `4px solid ${tint}`, borderRadius: 8, padding: '10px 12px', background: '#fff' }}>
      <div style={{ ...bpLbl, color: tint }}>{label}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 150px', minWidth: 0 }}>
          <div style={{ fontSize: 10.5, color: '#9ca3af', marginBottom: 2 }}>Starts</div>
          <input type="date" value={date || ''} disabled={!canEdit}
            onChange={e => onSave({ date: e.target.value })} style={fld} />
        </div>
        <div style={{ flex: '0 1 110px' }}>
          <div style={{ fontSize: 10.5, color: '#9ca3af', marginBottom: 2 }}>Days</div>
          <input type="number" min="0" step="1" value={days ?? ''} disabled={!canEdit} placeholder="—"
            onChange={e => onSave({ days: e.target.value })} style={fld} />
        </div>
      </div>
      <div style={{ fontSize: 12, marginTop: 6, fontWeight: (done || finish) ? 600 : 400,
        color: done ? '#15803d' : finish ? '#374151' : '#9ca3af' }}>
        {done ? 'Complete' : finish ? `Off the job ${finish}` : 'Set a date and a duration to see when it finishes.'}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
        {/* Ticking this fills the date in with today — that IS what ready now means,
            and typing today's date underneath it would be saying it twice. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700,
          color: ready ? '#15803d' : '#6b7280', cursor: canEdit ? 'pointer' : 'default' }}>
          <input type="checkbox" checked={!!ready} disabled={!canEdit}
            onChange={e => onSave({ ready: e.target.checked })}
            style={{ width: 16, height: 16, accentColor: '#16a34a', cursor: canEdit ? 'pointer' : 'default' }} />
          Ready now
        </label>
        <button onClick={() => canEdit && onSave({ filler: !filler })} disabled={!canEdit}
          title="Work that can be dropped into a gap in the schedule"
          style={{ padding: '5px 12px', borderRadius: 999, font: 'inherit', fontWeight: 700,
            fontSize: 12, cursor: canEdit ? 'pointer' : 'default',
            border: `1px solid ${filler ? '#a16207' : '#e5e7eb'}`,
            background: filler ? '#fef3c7' : '#fff', color: filler ? '#854d0e' : '#9ca3af' }}>
          {filler ? '✓ Filler work' : 'Filler work'}
        </button>

        {/* Complete sits at the far end, away from the two that mean "not started
            yet" — it's the opposite end of the line's life, so it reads as one. */}
        <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5,
          fontWeight: 700, color: done ? '#15803d' : '#6b7280', cursor: canEdit ? 'pointer' : 'default' }}>
          <input type="checkbox" checked={!!done} disabled={!canEdit}
            onChange={e => onSave({ done: e.target.checked })}
            style={{ width: 16, height: 16, accentColor: '#16a34a', cursor: canEdit ? 'pointer' : 'default' }} />
          Complete
        </label>
      </div>
    </div>
  );
}

// A line only knows it's "dirt" or "elec"; this turns what it changed into the
// field name the server stores it under.
function bpPatch(line, v) {
  if ('date' in v)   return { [`${line}_date`]: v.date };
  if ('days' in v)   return { [`${line}_days`]: v.days };
  if ('ready' in v)  return { [`${line}_ready`]: v.ready };
  if ('done' in v)   return { [`${line}_done`]: v.done };
  return { [`${line}_filler`]: v.filler };
}

export function BigPicturePillow({ projectId, canEdit = true }) {
  const { data, reload } = useLoad(`/projects/${projectId}/big-picture`, [projectId]);
  const [error, setError] = useState('');
  const plan = data?.plan || {};
  const history = data?.history || [];

  async function save(patch) {
    setError('');
    try {
      await apiFetch(`/projects/${projectId}/big-picture`, { method: 'PUT', body: JSON.stringify(patch) });
      await reload();
    } catch (e) { setError(e?.error || 'Could not save that.'); }
  }

  if (!data) return <div style={muted}>Loading the schedule…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Ready now and filler work belong to the LINE, not the job: the dirt can be
          ready to go while the electrical is still waiting on a permit. */}
      <BigPictureLine label="Dirt" tint="#a16207" date={plan.dirt_date} days={plan.dirt_days}
        ready={plan.dirt_ready} filler={plan.dirt_filler} done={plan.dirt_done} canEdit={canEdit}
        onSave={v => save(bpPatch('dirt', v))} />
      <BigPictureLine label="Electrical" tint="#1d4ed8" date={plan.elec_date} days={plan.elec_days}
        ready={plan.elec_ready} filler={plan.elec_filler} done={plan.elec_done} canEdit={canEdit}
        onSave={v => save(bpPatch('elec', v))} />

      {error && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 600 }}>{error}</div>}

      <div>
        <div style={bpLbl}>Changes {history.length ? `(${history.length})` : ''}</div>
        {history.length === 0 ? (
          <div style={muted}>No changes yet — once a date moves, who moved it and by how much shows here.</div>
        ) : history.map(h => <BigPictureChange key={h.id} h={h} />)}
      </div>
    </div>
  );
}

const BP_FIELD_WORD = {
  dirt_date: 'dirt date', dirt_days: 'dirt duration',
  elec_date: 'electrical date', elec_days: 'electrical duration',
  dirt_ready: 'dirt ready now', dirt_filler: 'dirt filler work',
  elec_ready: 'electrical ready now', elec_filler: 'electrical filler work',
  dirt_done: 'dirt complete', elec_done: 'electrical complete',
};

function BigPictureChange({ h }) {
  const isDate = h.field.endsWith('_date');
  const isFlag = h.field.endsWith('_ready') || h.field.endsWith('_filler') || h.field.endsWith('_done');
  const val = v => (v == null || v === '' ? 'nothing'
    : isDate ? shortDate(v + 'T00:00:00')
    : isFlag ? (v === '1' ? 'yes' : 'no')
    : `${v} days`);
  // The slip is the number worth reading, so it gets the colour: red pushed out,
  // green pulled in.
  const slip = h.delta_days == null ? null : Number(h.delta_days);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, padding: '4px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ fontWeight: 700, color: '#111' }}>{h.changed_by_name}</span>
      <span style={{ color: '#6b7280', flex: 1, minWidth: 0 }}>
        {BP_FIELD_WORD[h.field] || h.field} {val(h.old_value)} → {val(h.new_value)}
        {slip !== null && (
          <strong style={{ marginLeft: 6, color: slip > 0 ? '#b91c1c' : '#15803d' }}>
            {slip > 0 ? '+' : ''}{slip} day{Math.abs(slip) === 1 ? '' : 's'}
          </strong>
        )}
      </span>
      <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>{shortDate(h.changed_at)}</span>
    </div>
  );
}
